import React, { useState, useEffect } from "react";
import { Card, Typography, Select, Button, DatePicker, Table, Space, Popover } from "antd";
import {
  useFetchReportsByPeriodMutation,
  useFetchReportsQuery,
  useDeleteReportMutation,
} from "./reports.service";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import isoWeek from "dayjs/plugin/isoWeek";
import "./style.css";
import { CloudDownloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useTimezone } from "../../provider/timezoneprovider";
import ProgressBar from "./progressbar";
import { useFetchChecklistSettingsQuery } from "../settings/settings.service";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

const { Column } = Table;

export default function Reports() {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { timezone } = useTimezone();

  const [period, setPeriod] = useState("thisWeek");
  const [startDate, setStartDate] = useState(() => {
    const now = dayjs().tz(timezone);
    return [
      now.startOf('isoWeek').startOf('day'),
      now.endOf('isoWeek').startOf('day')
    ];
  });
  
  const [submitLoading, setSubmitLoading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const now = dayjs().tz(timezone);

    if (period === "thisWeek") {
      setStartDate([
        dayjs.tz(now.startOf("isoWeek").format("YYYY-MM-DD"), timezone),
        dayjs.tz(now.endOf("isoWeek").format("YYYY-MM-DD"), timezone),
      ]);
    } else if (period === "thisMonth") {
      setStartDate([
        dayjs.tz(now.startOf("month").format("YYYY-MM-DD"), timezone),
        dayjs.tz(now.endOf("month").format("YYYY-MM-DD"), timezone),
      ]);
    }
  }, [period, timezone]);

  const {  checklistSettingsResponse } = useFetchChecklistSettingsQuery();
  const checklistSettings = checklistSettingsResponse?.data || checklistSettingsResponse || {};
  const showMonthly = checklistSettings?.monthly === true;

  const handleChange = (e) => {
    setPeriod(e);
  };

  // Helper: Clean HTML and Convert <li> to Newlines
  const formatListContent = (html) => {
    if (!html) return "";
    let text = html.replace(/<li>/g, "\n• ");
    text = text.replace(/<\/?[^>]+(>|$)/g, "");
    text = text.replace(/&nbsp;/g, " ");
    return text.trim();
  };

  // ================= PDF GENERATION FUNCTION =================
  const generatePDF = async (responseData) => {
    setSubmitLoading(true);
    
    try {
     
    // Better page setup with proper margins
    const doc = new jsPDF({ 
      orientation: "portrait", 
      unit: "mm", 
      format: "a4",
      putOnlyUsedFonts: true,
      compress: true
    });
    const loadImage = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";

    img.onload = () => resolve(img);
    img.onerror = reject;

    img.src = url;
  });
};
      
const margin = 12;  // Reduce from 15 to 12
const pageWidth = doc.internal.pageSize.getWidth();
const pageHeight = doc.internal.pageSize.getHeight();
const contentWidth = pageWidth - (margin * 2);
    let finalY = 25; // Start a bit lower for better top margin

      const headerColor = [51, 51, 51];
      const sectionHeaderBg = [243, 244, 246];
      const accentColor = [129, 181, 50];
      const incidentHeaderBg = [128, 0, 0];

      const checkPage = (requiredSpace = 20) => {
        if (finalY > pageHeight - requiredSpace) {
          doc.addPage();
          finalY = 20;
          return true;
        }
        return false;
      };

      // Header
const logoUrl = "./mark.jpg";

try {
  const img = await loadImage(logoUrl);

  const maxWidth = 100;
  const maxHeight = 20;

  let width = img.width;
  let height = img.height;

  const ratio = Math.min(maxWidth / width, maxHeight / height);
  width *= ratio;
  height *= ratio;

  // IMPORTANT: pass img.src OR canvas base64 (not img object)
  doc.addImage(img, "JPEG", margin, finalY, width, height);

  // finalY += height + 5; // spacing fix

} catch (e) {
  console.warn("Logo not loaded", e);
}

      doc.setFontSize(22);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...headerColor);
      doc.text("Checklist Report", margin + 40, finalY + 8 );
      
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${moment().format("MMMM D, YYYY h:mm A")}`, margin + 40, finalY + 15);
      
      finalY += 30;
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, finalY, pageWidth - margin, finalY);
      finalY += 10;

      // Data preparation
      const allTasks = [
        ...(responseData.openingTasks || []).map(t => ({ ...t, type: "opening", label: "Opening Tasks" })),
        ...(responseData.hotholdTasks || []).map(t => ({ ...t, type: "hothold", label: "Probe Tasks" })),
        ...(responseData.closingTasks || []).map(t => ({ ...t, type: "closing", label: "Closing Tasks" })),
        ...(responseData.weeklyTasks || []).map(t => ({ ...t, type: "weekly", label: "Weekly Tasks" })),
        ...(responseData.monthlyTasks || []).map(t => ({ ...t, type: "monthly", label: "Monthly Tasks" })),
      ];

      allTasks.sort((a, b) => (a.taskNumber || 0) - (b.taskNumber || 0));
      // Group by date
      const tasksByDate = {};
      allTasks.forEach(task => {
    const utcDate = dayjs(task.createdAt).utc();
  const date = utcDate.format("YYYY-MM-DD");
  
  if (!tasksByDate[date]) tasksByDate[date] = [];
  tasksByDate[date].push(task);
});

      const sortedDates = Object.keys(tasksByDate).sort();

      // Render each date
      for (const date of sortedDates) {
        const dayTasks = tasksByDate[date];

  const displayDate = dayjs.utc(date).format("yyyy-MM-DD");
        
        checkPage(20);
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 102, 204);
        doc.text(`${dayjs.utc(date).format("YYYY-MM-DD")}`, margin, finalY);
        finalY += 2;
        // doc.setLineWidth(0.5);
        // doc.setDrawColor(200, 200, 200);
        // doc.line(margin, finalY, pageWidth - margin, finalY);
        // finalY += 8;

        const typesOrder = ["opening", "hothold", "closing", "weekly", "monthly"];
        
        for (const typeKey of typesOrder) {
          const typeTasks = dayTasks.filter(t => t.type === typeKey);
          if (typeTasks.length === 0) continue;

          const actionCount = typeTasks.filter(t => t.comment && t.comment.toString().trim() !== '').length;
          const firstTask = typeTasks[0];
          
          checkPage(15);

          const tableBody = [];
          
     // ==================== TASK RENDERING WITH FLUTTER STYLE LOGIC ====================
for (const task of typeTasks) {
  const rawTitle = task.title_en || task.title_ar || task.title || "";
  const mainTitle = formatListContent(rawTitle);

  // ========== 1. SECTION HEADER ==========
  if (task.isSection === true) {
    tableBody.push([
      { 
        content: mainTitle, 
        colSpan: 2,
        styles: { 
          fontSize: 11, 
          cellPadding: 8, 
          overflow: 'linebreak', 
          valign: 'top',           
          halign: 'left',
          fontStyle: 'bold',
          fillColor: [230, 240, 255]  // Light blue (#e6f0ff)
        } 
      },
      {}
    ]);
    continue;
  }

  // ========== 2. CHECK COMMENT (HIGHEST PRIORITY) ==========
  else if (task.comment && task.comment.toString().trim() !== '') {
    let commentText = "";
    if (typeof task.comment === 'string') {
      commentText = formatListContent(task.comment);
    } else if (typeof task.comment === 'object') {
      const langKey = `comment_${language}`;
      commentText = formatListContent(task.comment[langKey] || task.comment.en || task.comment.default || "");
    }
    
    const hasTemperature = task.isTempSlider === true;
    const tempValue = hasTemperature ? `${(task.tempSlider || 0).toFixed(1)}°C` : "";
    const isCompletedStatus = task.status === "completed";
    
    if (hasTemperature && isCompletedStatus) {
      // Comment + Temperature + Completed - Orange background
      tableBody.push([
        { 
          content: mainTitle, 
          styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak', valign: 'top', halign: 'left' } 
        },
        { 
          content: `NOTE: ${commentText}\n${tempValue}`, 
          styles: { 
            fontSize: 8, 
            halign: 'left', 
            cellPadding: 5, 
            overflow: 'linebreak', 
            valign: 'top',
            fillColor: [249, 115, 22],  // Orange #f97316
            textColor: [255, 255, 255]
          } 
        }
      ]);
    } 

    
    
    
    else if (hasTemperature) {
      // Comment + Temperature (not completed) - Orange background
      tableBody.push([
        { 
          content: mainTitle, 
          styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak', valign: 'top', halign: 'left' } 
        },
        { 
          content: `NOTE: ${commentText}\n${tempValue}`, 
          styles: { 
            fontSize: 8, 
            halign: 'left', 
            cellPadding: 5, 
            overflow: 'linebreak', 
            valign: 'top',
            fillColor: [249, 115, 22],
            textColor: [255, 255, 255]
          } 
        }
      ]);
    } else {
      // Only comment - Orange background
      tableBody.push([
        { 
          content: mainTitle, 
          styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak', valign: 'top', halign: 'left' } 
        },
        { 
          content: `NOTE: ${commentText}`, 
          styles: { 
            fontSize: 8, 
            halign: 'left', 
            cellPadding: 5, 
            overflow: 'linebreak', 
            valign: 'top',
            fillColor: [249, 115, 22],
            textColor: [255, 255, 255]
          } 
        }
      ]);
    }
  }

 // ========== 3. DATE RECORD (MUST BE BEFORE isInput) ==========
else if (task.isDateRecord === true) {
  const isCompletedStatus = task.status === "completed";
  
  // Get content
  const langKey = `content_${language}`;
  let contentValue = "";
  if (task[langKey]) {
    contentValue = formatListContent(task[langKey]);
  } else if (task.content_en) {
    contentValue = formatListContent(task.content_en);
  } else if (task.content_ar) {
    contentValue = formatListContent(task.content_ar);
  }
  
  // Get slider value
  const hasSlider = task.isTempSlider === true;
  const sliderValue = hasSlider ? `${(task.tempSlider || 0).toFixed(1)}°C` : "";
  
  // ✅ FIX: Get time from completedAt or createdAt
  let displayTime = "";
  if (isCompletedStatus) {
    // Try completedAt first
    if (task.completedAt) {
      if (typeof task.completedAt === 'string') {
        displayTime = moment(task.completedAt).format("MMM D, YYYY h:mm A");
      } else if (task.completedAt.$date) {
        displayTime = moment(task.completedAt.$date).format("MMM D, YYYY h:mm A");
      }
    }
    // If no completedAt, use createdAt
    if (!displayTime && task.createdAt) {
      if (typeof task.createdAt === 'string') {
        displayTime = moment(task.createdAt).format("MMM D, YYYY h:mm A");
      } else if (task.createdAt.$date) {
        displayTime = moment(task.createdAt.$date).format("MMM D, YYYY h:mm A");
      }
    }
  }
  
  let rightHtml = "";
  let rightSideBgColor = null;
  let rightSideTextColor = [0, 0, 0];
  
  if (isCompletedStatus && displayTime) {
    rightSideBgColor = [129, 181, 50];  // Green background
    rightSideTextColor = [255, 255, 255];
    
    const parts = [];
    if (contentValue) parts.push(contentValue);
    if (sliderValue) parts.push(sliderValue);
    parts.push(`${displayTime}`);
    
    rightHtml = parts.join('\n');
  } else if (contentValue) {
    rightHtml = contentValue;
  } else if (sliderValue) {
    rightHtml = sliderValue;
  }
  
  tableBody.push([
    { 
      content: mainTitle, 
      styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak', valign: 'top', halign: 'left' } 
    },
    { 
      content: rightHtml, 
      styles: { 
        fontSize: 9, 
        halign: 'left', 
        cellPadding: 5, 
        overflow: 'linebreak', 
        valign: 'top',
        fillColor: rightSideBgColor,
        textColor: rightSideTextColor
      } 
    }
  ]);
}

  // ========== 3. CHECK INPUT (isInput) ==========
  else if (task.isInput === true) {
    const langKey = `content_${language}`;
    let inputValue = "";
    
    if (task[langKey]) {
      inputValue = formatListContent(task[langKey]);
    } else if (task.content_en) {
      inputValue = formatListContent(task.content_en);
    } else if (task.content_ar) {
      inputValue = formatListContent(task.content_ar);
    } else {
      inputValue = task.status === "completed" ? "Completed" : "Not Completed";
    }
    
    const hasTemperature = task.isTempSlider === true;
    const tempValue = hasTemperature ? `${(task.tempSlider || 0).toFixed(1)}°C` : "";
    const isCompletedStatus = task.status === "completed";
    
    if (hasTemperature && isCompletedStatus) {
      // Input + Temperature + Completed - Green background
      tableBody.push([
        { 
          content: mainTitle, 
          styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak', valign: 'top', halign: 'left' } 
        },
        { 
          content: `${inputValue}\n${tempValue}`, 
          styles: { 
            fontSize: 8, 
            halign: 'left', 
            cellPadding: 5, 
            overflow: 'linebreak', 
            valign: 'top',
            fillColor: [129, 181, 50],  // Green #81b532
            textColor: [255, 255, 255]
          } 
        }
      ]);
    } else if (hasTemperature) {
      // Only temperature (not completed) - No background
      tableBody.push([
        { 
          content: mainTitle, 
          styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak', valign: 'top', halign: 'left' } 
        },
        { 
          content: inputValue || tempValue, 
          styles: { fontSize: 9, halign: 'left', cellPadding: 3, overflow: 'linebreak', valign: 'top' } 
        }
      ]);
    } else {
      // Only text input - Green bg if completed
      tableBody.push([
        { 
          content: mainTitle, 
          styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak', valign: 'top', halign: 'left' } 
        },
        { 
          content: inputValue || "Not Completed", 
          styles: { 
            fontSize: 9, 
            halign: 'left', 
            cellPadding: 3, 
            overflow: 'linebreak', 
            valign: 'top',
            fillColor: isCompletedStatus ? [129, 181, 50] : [255, 255, 255],
            textColor: isCompletedStatus ? [255, 255, 255] : [0, 0, 0]
          } 
        }
      ]);
    }
  }

  // ========== 4. TEMPERATURE SLIDER (isTempSlider) ==========
  else if (task.isTempSlider) {
    const tempValue = `${(task.tempSlider || 0).toFixed(1)}°C`;
    const isCompletedStatus = task.status === "completed";
    
    if (isCompletedStatus) {
      // Completed + Temperature - Green background
      tableBody.push([
        { 
          content: mainTitle, 
          styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak', valign: 'top', halign: 'left' } 
        },
        { 
          content: tempValue, 
          styles: { 
            fontSize: 10, 
            halign: 'left', 
            cellPadding: 5, 
            overflow: 'linebreak', 
            valign: 'top',
            fontStyle: 'bold',
            fillColor: [129, 181, 50],
            textColor: [255, 255, 255]
          } 
        }
      ]);
    } else {
      // Not completed - No background, black text
      tableBody.push([
        { 
          content: mainTitle, 
          styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak', valign: 'top', halign: 'left' } 
        },
        { 
          content: tempValue, 
          styles: { fontSize: 9, halign: 'left', cellPadding: 3, overflow: 'linebreak', valign: 'top', fontStyle: 'bold' } 
        }
      ]);
    }
  }

 // ========== TEMPERATURE SENSOR (isTemperature) - FLUTTER STYLE ==========
else if (task.isTemperature === true) {
  const temperatures = task.temperature;
  const hasData = temperatures && Array.isArray(temperatures) && temperatures.length > 0;
  
  if (!hasData) {
    // ❌ NO DATA - White background, black text
    tableBody.push([
      { 
        content: mainTitle, 
        styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak', valign: 'top', halign: 'left' } 
      },
      { 
        content: "Not Recorded", 
        styles: { 
          fontSize: 9, 
          halign: 'left', 
          cellPadding: 5, 
          overflow: 'linebreak', 
          valign: 'top',
          fillColor: [255, 255, 255],  // White background
          textColor: [0, 0, 0]         // Black text
        } 
      }
    ]);
  } else {
    // ✅ HAS DATA - Title row with "Recorded"
    tableBody.push([
      { 
        content: mainTitle, 
        styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak', valign: 'top', halign: 'left', fontStyle: 'bold' } 
      },
      { 
        content: "Recorded", 
        styles: { 
          fontSize: 9, 
          halign: 'left', 
          cellPadding: 5, 
          overflow: 'linebreak', 
          fontStyle: 'bold',
          fillColor: [129, 181, 50],   // Green background
          textColor: [255, 255, 255]   // White text
        } 
      }
    ]);
    
    // Sensor rows - Green background, white text
    for (const temp of temperatures) {
      const deviceId = temp.device_id || "Sensor";
      const tempValue = temp.temperature ?? 0;
      const num = typeof tempValue === 'number' ? tempValue : parseFloat(tempValue);
      
      if (!isNaN(num)) {
        tableBody.push([
          { 
            content: `  ${deviceId}`, 
            styles: { fontSize: 9, cellPadding: 2, overflow: 'linebreak', valign: 'top', halign: 'left' } 
          },
          { 
            content: `${num.toFixed(1)}°C`, 
            styles: { 
              fontSize: 9, 
              cellPadding: 2, 
              overflow: 'linebreak', 
              valign: 'top', 
              fontStyle: 'bold',
              fillColor: [129, 181, 50],   // Green background
              textColor: [255, 255, 255]   // White text
            } 
          }
        ]);
      }
    }
  }
}
  // ========== 7. DEFAULT: Simple Yes/No ==========
  else {
    let rightContent = "";
    const langKey = `content_${language}`;
    
    if (task[langKey]) rightContent = formatListContent(task[langKey]);
    else if (task.content_en) rightContent = formatListContent(task.content_en);
    else if (task.content_ar) rightContent = formatListContent(task.content_ar);
    else rightContent = task.status === "completed" ? "Yes" : "Not Completed";
    
    const isSimpleCompleted = task.status === "completed" && !task.content_en && !task.isTemperature && !task.isTempSlider;
    
    tableBody.push([
      { 
        content: mainTitle, 
        styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak', valign: 'top', halign: 'left' } 
      },
      { 
        content: rightContent, 
        styles: { 
          fontSize: 8, 
          halign: 'left', 
          cellPadding: 5, 
          overflow: 'linebreak', 
          valign: 'top',
          fillColor: isSimpleCompleted ? [129, 181, 50] : [255, 255, 255],
          textColor: isSimpleCompleted ? [255, 255, 255] : [0, 0, 0]
        } 
      }
    ]);
  }
}

autoTable(doc, {
  startY: finalY,
  head: [[
    { content: `${firstTask.label} (${typeTasks.length})`, colSpan: 1, styles: { fillColor: sectionHeaderBg, textColor: headerColor, fontStyle: 'bold', fontSize: 9, halign: 'left', cellPadding: 5 } },
    { content: `Action: ${actionCount}`, colSpan: 1, styles: { fillColor: sectionHeaderBg, textColor: headerColor, fontStyle: 'normal', fontSize: 9, halign: 'right', cellPadding: 5 } }
  ]],
  body: tableBody,
  columnStyles: { 
    0: { cellWidth: 'auto' },  // Left column auto width
    1: { cellWidth: 60 }       // Right column FIXED at 60mm (was 40mm)
  },
  headStyles: { minCellHeight: 10, lineWidth: 0, lineColor: [255, 255, 255] },
  bodyStyles: { lineWidth: 0.1, lineColor: [200, 200, 200] },
  margin: { left: margin, right: margin },
  tableWidth: 'auto',
  didDrawPage: (data) => { finalY = data.cursor.y; }
});
          
          finalY += 5;
        }
        
        finalY += 5;
      }

// ==================== INCIDENTS SECTION (Flutter Style - Light Blue Header) ====================
checkPage(30);

// Header - Light blue background (#e6f0ff)
doc.setFillColor(230, 240, 255);
doc.rect(margin, finalY, pageWidth - (margin * 2), 10, 'F');

doc.setFontSize(12);
doc.setFont("helvetica", "bold");
doc.setTextColor(51, 51, 51);
doc.text("Incidents", margin + 5, finalY + 7);

// Count on right side
const incidentCount = responseData.incidents?.length || 0;
doc.setFont("helvetica", "normal");
doc.text(`${incidentCount}`, pageWidth - margin - 10, finalY + 7, { align: 'right' });

finalY += 12;
doc.setDrawColor(200, 200, 200);
doc.line(margin, finalY, pageWidth - margin, finalY);
finalY += 6;

if (responseData.incidents && responseData.incidents.length > 0) {
  autoTable(doc, {
    startY: finalY,
    head: [["Issue", "Employee", "Time", "Resolution"]],
    body: responseData.incidents.map(inc => [
      formatListContent(inc.incident_en || inc.incident || ""),
      inc.employeeName || inc.employee || "-",
      moment(inc.createdAt).format("MMM D, YYYY - hh:mm A"),
      formatListContent(inc.resolution_en || inc.resolution || "")
    ]),
    headStyles: { 
      fillColor: [243, 244, 246],  // Light grey for table header
      textColor: [0,0,0], 
      fontStyle: 'bold', 
      fontSize: 9,
      halign: 'left',
      cellPadding: 4
    },
    bodyStyles: { 
      fontSize: 8, 
      cellPadding: 4, 
      overflow: 'linebreak', 
      valign: 'top' 
    },
    columnStyles: {
      0: { cellWidth: '35%' },
      1: { cellWidth: '15%' },
      2: { cellWidth: '20%' },
      3: { cellWidth: '30%' }
    },
    margin: { left: margin, right: margin },
    didDrawPage: (data) => { finalY = data.cursor.y; }
  });
} else {
  doc.setFontSize(10);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(150, 150, 150);
  doc.text("No incidents recorded for this period.", margin, finalY + 5);
  finalY += 10;
}
finalY += 10;
// ==================== CHECKLIST INCIDENTS SECTION (Flutter Style - Light Blue Header) ====================
const checklistIncidents = allTasks.filter(t => t.comment && t.comment.toString().trim() !== '');

// Group by task type
const groupedChecklist = {
  "Opening Tasks": checklistIncidents.filter(t => t.type === "opening"),
  "Probe / Hot Hold Tasks": checklistIncidents.filter(t => t.type === "hothold"),
  "Closing Tasks": checklistIncidents.filter(t => t.type === "closing"),
  "Weekly Tasks": checklistIncidents.filter(t => t.type === "weekly"),
  "Monthly Tasks": checklistIncidents.filter(t => t.type === "monthly"),
};

Object.keys(groupedChecklist).forEach(key => {
  if (groupedChecklist[key].length === 0) delete groupedChecklist[key];
});

const totalChecklistIncidents = checklistIncidents.length;

checkPage(30);

// Header - Light blue background (#e6f0ff)
doc.setFillColor(230, 240, 255);
doc.rect(margin, finalY, pageWidth - (margin * 2), 10, 'F');

doc.setFontSize(12);
doc.setFont("helvetica", "bold");
doc.setTextColor(51, 51, 51);
doc.text("Checklist Incidents", margin + 5, finalY + 7);

// Count on right side
doc.setFont("helvetica", "normal");
doc.text(`${totalChecklistIncidents}`, pageWidth - margin - 10, finalY + 7, { align: 'right' });

finalY += 12;
doc.setDrawColor(200, 200, 200);
doc.line(margin, finalY, pageWidth - margin, finalY);
finalY += 6;

if (totalChecklistIncidents > 0) {
  let currentY = finalY;
  
  for (const [groupName, incidents] of Object.entries(groupedChecklist)) {
    if (incidents.length === 0) continue;
    
    checkPage(20);
    
    // Group Title - Light background with left border (like Flutter)
    const titleHeight = 10;
    doc.setFillColor(245, 245, 245);  // #f5f5f5
    doc.rect(margin, currentY, pageWidth - (margin * 2), titleHeight, 'F');
    
    // // Left border (Flutter style)
    // doc.setDrawColor(47, 46, 112);  // #2F2E70
    // doc.setLineWidth(1.5);
    // doc.line(margin, currentY, margin, currentY + titleHeight);
    
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(groupName, margin + 8, currentY + 7);
    currentY += titleHeight + 4;
    
    // Each incident row
    for (const item of incidents) {
      checkPage(12);
      
      const comment = formatListContent(item.comment || "");
      const incidentDateTime = moment(item.createdAt).format("MMM DD, YYYY - hh:mm A");
      
      // Draw row with bottom border
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 100, 100);
      doc.text(incidentDateTime, margin + 5, currentY + 4);
      
      // Comment on right side (Red color - Flutter style)
      doc.setTextColor(255, 0, 0);
      doc.setFont("helvetica", "bold");
      const commentWidth = doc.getTextWidth(comment);
      const commentX = pageWidth - margin - 5 - commentWidth;
      doc.text(comment, commentX, currentY + 4);
      
      // Reset color
      doc.setTextColor(0, 0, 0);
      
      // Bottom border
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(margin, currentY + 8, pageWidth - margin, currentY + 8);
      
      currentY += 12;
    }
    
    currentY += 5;
  }
  
  finalY = currentY;
} else {
  doc.setFontSize(10);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(150, 150, 150);
  doc.text("No checklist incidents recorded for this period.", margin, finalY + 5);
  finalY += 10;
}
finalY += 10;

// ==================== DELIVERIES SECTION (Flutter Style - Complete) ====================
checkPage(30);

// Header - Light blue background (#e6f0ff) - SAME AS FLUTTER
doc.setFillColor(230, 240, 255);
doc.rect(margin, finalY, pageWidth - (margin * 2), 10, 'F');

doc.setFontSize(12);
doc.setFont("helvetica", "bold");
doc.setTextColor(51, 51, 51);
doc.text("Deliverys", margin + 5, finalY + 7);

// Count on right side
const deliveryCount = responseData.deliveries?.length || 0;
doc.setFont("helvetica", "normal");
doc.text(`${deliveryCount}`, pageWidth - margin - 10, finalY + 7, { align: 'right' });

finalY += 12;
doc.setDrawColor(200, 200, 200);
doc.line(margin, finalY, pageWidth - margin, finalY);
finalY += 6;

if (responseData.deliveries && responseData.deliveries.length > 0) {
  // Format delivery data - SAME AS FLUTTER
  const deliveryRows = responseData.deliveries.map(d => {
    // Format createdAt
    let createdAtStr = "-";
    try {
      if (d.createdAt) {
        createdAtStr = moment(d.createdAt).format("MMM DD, YYYY - hh:mm A");
      }
    } catch(e) {
      createdAtStr = "-";
    }
    
    // Format temperature
    let tempStr = "-";
    if (d.temperature !== null && d.temperature !== undefined) {
      tempStr = `${parseFloat(d.temperature).toFixed(1)}°C`;
    }
    
    return [
      createdAtStr,
      d.supplierName || "-",
      d.deliveryType || "-",
      d.isIntact ? "Yes" : "No",
      tempStr,
      d.invoiceRef || "-"
    ];
  });

  autoTable(doc, {
    startY: finalY,
    head: [["Created At", "Supplier Name", "Delivery Type", "Is-Intact", "Temperature", "Invoice Ref"]],
    body: deliveryRows,
    headStyles: { 
      fillColor: [243, 244, 246],  // Light grey for table header
      textColor: [0, 0, 0], 
      fontStyle: 'bold', 
      fontSize: 8,
      halign: 'center',
      cellPadding: 4
    },
    bodyStyles: { 
      fontSize: 7.5, 
      cellPadding: 3, 
      overflow: 'linebreak',
      valign: 'middle'
    },
    columnStyles: {
      0: { cellWidth: '22%', halign: 'center' },   // Created At
      1: { cellWidth: '20%', halign: 'left' },     // Supplier Name
      2: { cellWidth: '15%', halign: 'left' },     // Delivery Type
      3: { cellWidth: '10%', halign: 'center' },   // Is-Intact
      4: { cellWidth: '13%', halign: 'center', fontStyle: 'bold' },  // Temperature - BOLD
      5: { cellWidth: '20%', halign: 'left' }      // Invoice Ref
    },
    margin: { left: margin, right: margin },
    didDrawPage: (data) => { finalY = data.cursor.y; }
  });
} else {
  doc.setFontSize(10);
  doc.setFont("helvetica", "italic");
  doc.setTextColor(150, 150, 150);
  doc.text("No deliveries recorded for this period.", margin, finalY + 5);
  finalY += 10;
}
finalY += 10;
      const fileName = `report-${moment().format("YYYY-MM-DD-HH-mm")}.pdf`;
      doc.save(fileName);
      
      toast.success("Report generated successfully");

    } catch (error) {
      console.error("PDF Generation Error:", error);
      toast.error("Error generating PDF");
    } finally {
      setSubmitLoading(false);
      setProgress(0);
    }
  };

  // ================= API HANDLERS =================
const [requestFetchReportsByPeriod, { isLoading: isFetchingReport }] = useFetchReportsByPeriodMutation();

const { data: reportData = { data: { history: [], pagination: {} } } } = useFetchReportsQuery("", {
  refetchOnMountOrArgChange: true,
});
const handleDateChange = (dates) => {
  if (dates && dates.length === 2) {
    const start = dayjs.tz(dates[0].format("YYYY-MM-DD") + "T00:00:00", timezone).startOf("day");
    const end = dayjs.tz(dates[1].format("YYYY-MM-DD") + "T00:00:00", timezone).startOf("day");
    setStartDate([start, end]);
  }
};
  const handleReportsDownload = async (item) => {
    setSubmitLoading(true);
    try {
      let payload = { ...item };

      if (payload.startDate && typeof payload.startDate === 'number') {
        payload.startDate = dayjs(payload.startDate).format('YYYY-MM-DD');
      }
      if (payload.endDate && typeof payload.endDate === 'number') {
        payload.endDate = dayjs(payload.endDate).format('YYYY-MM-DD');
      }

      const response = await requestFetchReportsByPeriod(payload).unwrap();
      const reportData = response?.reportData || response?.data?.reportData;

      if (reportData) {
        await generatePDF(reportData);
      } else {
        throw new Error("No report data found in response");
      }
    } catch (error) {
      console.error("Download Error:", error);
      const errorMsg = error?.data?.message || error?.message || t("Failed to download report");
      toast.error(errorMsg);
      setSubmitLoading(false);
    }
  };
  
const { history, pagination } = reportData?.data || { history: [], pagination: {} };
const [deleteReport] = useDeleteReportMutation();

  return (
    <>
      <Card className="reports-page" bordered={false}>
        <div className="reports-page-container">
          <h2>{t("Select your report frequency")}</h2>
          <p>{t("Please choose the report range below")}</p>
          <Typography.Title level={5}>{t("Time Period")}</Typography.Title>
          <Select
            style={{ width: "100%" }}
            value={period}
            onChange={handleChange}
            options={[
              { label: t("thisWeek"), value: "thisWeek" },
              { label: t("thisMonth"), value: "thisMonth" },
              { label: t("Custom"), value: "custom" },
            ]}
          />

          <div className="reports-page-container-datepicker">
            <div className="reports-page-container-datepicker-start">
              <DatePicker.RangePicker
                style={{ width: "100%" }}
                disabled={period === "custom" ? false : true}
                value={[
                  startDate[0] ? dayjs(startDate[0]).tz(timezone) : null,
                  startDate[1] ? dayjs(startDate[1]).tz(timezone) : null,
                ]}
                format="YYYY-MM-DD"
                onChange={handleDateChange}
                allowClear={false}
              />
            </div>
          </div>

          <div className="reports-page-container-description">
            {t("What the report will contain:")}
          </div>
          <p className="reports-page-container-description-content">
            {t("Opening Checks")} <br />
            {t("Probe Checks")} <br />
            {t("Closing Checks")} <br />
            {t("Weekly Checks")} <br />
            {showMonthly && <>{t("Monthly Checks")} <br /></>}
            {t("Temperature readings")} <br />
            {t("Incidents Logged")} <br />
            {t("Deliveries")}
          </p>
          
          <Button
            type="primary"
            loading={submitLoading}
            onClick={() => {
              if (period === "custom") {
                if (startDate && startDate.length === 2 && startDate[0] && startDate[1]) {
                  handleReportsDownload({
                    period,
                    startDate: dayjs(startDate[0]).tz(timezone).format("YYYY-MM-DD"),
                    endDate: dayjs(startDate[1]).tz(timezone).format("YYYY-MM-DD"),
                    log: true,
                  });
                } else {
                  toast.error(t("Please input the date"));
                }
              } else if (period === "thisWeek") {
                const startOfWeek = dayjs().tz(timezone).startOf('isoWeek');
                const endOfWeek = dayjs().tz(timezone).endOf('isoWeek');
                handleReportsDownload({
                  period,
                  startDate: startOfWeek.valueOf(),
                  endDate: endOfWeek.valueOf(),
                  log: true,
                });
              } else if (period === "thisMonth") {
                const startOfMonth = dayjs().tz(timezone).startOf('month');
                const endOfMonth = dayjs().tz(timezone).endOf('month');
                handleReportsDownload({
                  period,
                  startDate: startOfMonth.valueOf(),
                  endDate: endOfMonth.valueOf(),
                  log: true,
                });
              }
            }}
          >
            {t("Submit")}
          </Button>
        </div>

        <Card className="reports-page-list" title={
          <Space>
            {t("Reports Created")}
            <Popover
              content={
                <div style={{ maxWidth: 300 }}>
                  <h4>System Auto-Resets</h4>
                  <p><b>Daily 11:59 PM:</b> Opening, Closing, Probe tasks reset</p>
                  <p><b>Sunday 11:59 PM:</b> Weekly tasks reset</p>
                  <p><b>Month End 11:59 PM:</b> Monthly tasks reset</p>
                  <p>All completed tasks are logged to history</p>
                </div>
              }
              title="System Information"
              trigger="hover"
            >
              <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer' }} />
            </Popover>
          </Space>
        }>
          <Table dataSource={history} rowKey="_id">
            <Column
              title={t("Created")}
              dataIndex={"generated_at"}
              render={(generatedAt) => moment(generatedAt).format("YYYY-MM-DD HH:mm")}
              align="center"
            />
            <Column
              align="center"
              title={t("Date Range")}
              render={(_, record) => `${record.start_date?.slice(0, 10) || ""} - ${record.end_date?.slice(0, 10) || ""}`}
            />
            <Column
              align="center"
              title={t("Status")}
              render={(_, record) => (
                <div
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    handleReportsDownload({
                      period: record.period_type,
                      startDate: new Date(record.start_date).valueOf(),
                      endDate: new Date(record.end_date).valueOf(),
                      log: false,
                    })
                  }
                >
                  <CloudDownloadOutlined /> {t("Download")}
                </div>
              )}
            />
            <Column
              align="center"
              title={t("Actions")}
              render={(_, record) => (
                <Button
                  type="link"
                  danger
                  onClick={async () => {
                    try {
                      await deleteReport(record._id);
                      toast.success(t("Report deleted successfully"));
                    } catch (error) {
                      toast.error(t("Failed to delete report"));
                    }
                  }}
                >
                  {t("Delete")}
                </Button>
              )}
            />
          </Table>
        </Card>
        {submitLoading && <ProgressBar progress={progress} />}
      </Card>
    </>
  );
}